import { Box, Flex, PocketWatchIcon, Text, OpusdTokenIcon, CalculateOPXIcon } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'
import { PREDICTION_TOOLTIP_DISMISS_KEY } from 'config/constants'
import { useGetCurrentRoundCloseTimestamp } from 'state/predictions/hooks'
import { PredictionSupportedSymbol } from 'state/types'
import styled, { keyframes } from 'styled-components'
import { useConfig } from '../context/ConfigProvider'
import { formatRoundTime } from '../helpers'
import useCountdown from '../hooks/useCountdown'
import LabelPrice from './LabelPrice'
import usePollOraclePrice from '../hooks/usePollOraclePrice'

const TOKEN = 'FTM'

const TimerBox = styled(Box)`
  height: 50px;
  background-color: ${({ theme }) => theme.colors.cardHeaderBg};
  border-radius: 8px;
  padding: 5.5px 16px 5.5px 16px;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.colors.tertiary};

  width: fit-content;
  margin-left: auto;

  ${({ theme }) => theme.mediaQueries.xs} {
    gap: unset;
    width: fit-content;
  }

  @media screen and (max-width: 767px) {
    width: 100%;
    margin-left: 0;
  }
`

const Token = styled(Box)`
  z-index: 30;

  & > svg {
    height: 32px;
    width: 32px;
  }
`

const Title = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  line-height: normal;
`

const ClosingTitle = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  line-height: 140%;

  /* font-size: 9px;
  line-height: 21px;

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 16px;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    font-size: 20px;
    line-height: 22px;
  } */
`

const Interval = styled(Text)`
  ${({ theme }) => theme.mediaQueries.lg} {
    color: ${({ theme }) => theme.colors.white60};
    text-wrap: nowrap;
    /* width: 100px; */
  }

  /* ${({ theme }) => theme.mediaQueries.xs} {
    > span {
      display: none;
    }
  } */
  @media (max-width: 769px) {
    span {
      display: none;
    }
  }
`

const tooltipAnimation = keyframes`
  0%{
    opacity:0;
  }
  20%{
    opacity:1;
  }
  30%{
    transform: translateX(5px);
  }
  40%{
    transform: translateX(0px);
  }
  50%{
    transform: translateX(5px);
  }
  60%{
    transform: translateX(5px);
  }
  100%{
    opacity:1;
  }
`

export const Tooltip = styled.div`
  position: absolute;
  top: -5px;
  left: 55px;
  border-radius: 16px;
  padding: 16px;
  background: ${({ theme }) => theme.tooltip.background};
  box-shadow: ${({ theme }) => theme.tooltip.boxShadow};
  white-space: nowrap;
  opacity: 0;
  z-index: 100;
  animation: ${tooltipAnimation} 3s forwards ease-in-out;
  ${Text},svg {
    color: ${({ theme }) => theme.tooltip.text};
  }
  ${({ theme }) => theme.mediaQueries.md} {
    top: -10px;
    left: 81px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 21px;
    left: -6px;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    background: ${({ theme }) => theme.tooltip.background};
    transform: rotate(45deg);
  }
`

const Label = styled(Flex)<{ backgroundOpacity?: boolean }>`
  margin-left: 8px;
  position: relative;
  z-index: 1;
  box-shadow: ${({ theme }) => theme.shadows.level1};
  /* align-items: ${({ dir }) => (dir === 'right' ? 'flex-end' : 'center')}; */
  border-radius: ${({ dir }) => (dir === 'right' ? '8px 8px 8px 24px' : '8px 8px 24px 8px')};
  overflow: initial;
  justify-content: space-between;
  flex: 1;

  ${({ theme }) => theme.mediaQueries.lg} {
    /* align-items: flex-start; */
    border-radius: ${({ theme }) => theme.radii.medium};
    /* padding: ${({ dir }) => (dir === 'right' ? '8px 40px 8px 8px' : '5.5px 16px 5.5px 16px')}; */
    transition: 0.3s background-color ease-in-out;
    will-change: background-color;
  }
`

const StyledOpusdTokenIcon = styled(OpusdTokenIcon)`
  ${({ theme }) => theme.mediaQueries.xs} {
    width: 32px;
  }
`

export const PricePairLabel: React.FC<React.PropsWithChildren> = () => {
  const { token } = useConfig()
  const router = useRouter()
  const { price } = usePollOraclePrice()
  const [dismissTooltip, setDismissTooltip] = useState(() => {
    if (localStorage?.getItem(PREDICTION_TOOLTIP_DISMISS_KEY)) return true
    return false
  })

  const onDismissTooltip = useCallback(() => {
    localStorage?.setItem(PREDICTION_TOOLTIP_DISMISS_KEY, '1')
    setDismissTooltip(true)
  }, [])

  const onTokenSwitch = useCallback(() => {
    if (router.query.token === PredictionSupportedSymbol.CAKE) {
      router.query.token = PredictionSupportedSymbol.BNB
    } else if (router.query.token === undefined && token.symbol === PredictionSupportedSymbol.CAKE) {
      router.query.token = PredictionSupportedSymbol.BNB
    } else if (router.query.token === undefined && token.symbol === PredictionSupportedSymbol.BNB) {
      router.query.token = PredictionSupportedSymbol.CAKE
    } else if (token.symbol === undefined && router.query.token === undefined) {
      router.query.token = PredictionSupportedSymbol.BNB
    } else {
      router.query.token = PredictionSupportedSymbol.CAKE
    }
    if (!dismissTooltip) onDismissTooltip()

    router.replace(router, undefined, { scroll: false })
  }, [router, token, dismissTooltip, onDismissTooltip])

  // return null

  return (
    <>
      <Box pl={['0', '0', '0', '0', '0']} display="flex">
        {/* {!dismissTooltip && (
          <Tooltip>
            <Text mr="5px" display="inline-block" verticalAlign="super">
              {t('Switch pairs here.')}
            </Text>
            <CloseIcon cursor="pointer" onClick={onDismissTooltip} />
          </Tooltip>
        )} */}
        {/* <CoinSwitcher
          isDefaultBnb={router.query.token === 'BNB' || (router.query.token === undefined && token.symbol === 'BNB')}
          onTokenSwitch={onTokenSwitch}
        /> */}
        <StyledOpusdTokenIcon width="32px" />
        <Label alignItems="center" backgroundOpacity={!dismissTooltip}>
          <Title bold textTransform="uppercase">
            {/* {`${token.symbol}USD`} */}
            {TOKEN}/USD
          </Title>
          <LabelPrice price={price} />
        </Label>
      </Box>
    </>
  )
}

interface TimerLabelProps {
  interval: string
  unit: 'm' | 'h' | 'd'
}

export const TimerLabel: React.FC<React.PropsWithChildren<TimerLabelProps>> = ({ interval, unit }) => {
  const currentRoundCloseTimestamp = useGetCurrentRoundCloseTimestamp()
  const { secondsRemaining } = useCountdown(currentRoundCloseTimestamp)
  const countdown = formatRoundTime(secondsRemaining)
  const { t } = useTranslation()

  // console.log('test123', currentRoundCloseTimestamp, secondsRemaining, countdown)

  if (!currentRoundCloseTimestamp) {
    return null
  }

  return (
    <TimerBox pr="24px" display="flex">
      <Token display="flex">
        <PocketWatchIcon />
      </Token>
      <Label dir="left" alignItems="flex-start" flexDirection="column">
        {secondsRemaining !== 0 ? (
          <Title bold color="secondary">
            {countdown}
          </Title>
        ) : (
          <ClosingTitle bold color="secondary">
            {t('Closing')}
          </ClosingTitle>
        )}
        <Interval fontSize="12px">{`${interval}${t(unit)} per round`}</Interval>
      </Label>
    </TimerBox>
  )
}
